"use client";

import * as RadixToast from "@radix-ui/react-toast";
import * as React from "react";
import { type ReactNode } from "react";
import {
  AnimatedToastContent,
  ToastBearSprite,
  ToastText,
} from "@/components/ui/animated-toast-content";

type TxToastPhase = "confirm" | "pending" | "success" | "error";
type Count = { current: number; total: number };

export interface ToastData {
  id: string;
  phase: TxToastPhase;
  title: string;
  subtitle?: string;
  successImage?: string;
  count?: Count;
  hash?: `0x${string}`;
  open: boolean;
}

interface ToastContextValue {
  upsert: (toast: Omit<ToastData, "open"> & { open?: boolean }) => void;
  dismiss: (id: string) => void;
}

const ToastContext = React.createContext<ToastContextValue | null>(null);

export function useToasts() {
  const ctx = React.useContext(ToastContext);
  if (!ctx) throw new Error("useToasts must be used within <ToastProvider>");
  return ctx;
}

const BEAR_SPRITES: Record<
  TxToastPhase,
  { state: "confirming" | "pending" | "success" | "error"; src: string }
> = {
  confirm: { state: "confirming", src: "/poku/toast/info.png" },
  pending: { state: "pending", src: "/poku/toast/warning.png" },
  success: { state: "success", src: "/poku/toast/success.png" },
  error: { state: "error", src: "/poku/toast/fail.png" },
};

function ToastItem({ t, onOpenChange }: { t: ToastData; onOpenChange: (open: boolean) => void }) {
  const bear = BEAR_SPRITES[t.phase];
  // Friendlier auto-dismiss: shorter on success, a bit longer on error; long on intermediate states
  const duration = t.phase === "success" ? 5000 : t.phase === "error" ? 7000 : 600000;

  // Ensure auto-dismiss after entering a final state, even if the toast was opened earlier
  // (Radix duration does not reset when content changes from pending → success.)
  React.useEffect(() => {
    if (t.phase === "success" || t.phase === "error") {
      const d = t.phase === "success" ? 5000 : 7000;
      const timer = setTimeout(() => onOpenChange(false), d);
      return () => clearTimeout(timer);
    }
  }, [t.phase, onOpenChange]);

  return (
    <RadixToast.Root
      className="tv-toast-panel"
      duration={duration}
      type="foreground"
      open={t.open}
      onOpenChange={onOpenChange}
    >
      <AnimatedToastContent state={t.phase} id={t.id}>
        <div className="relative w-full" style={{ minWidth: "320px", maxWidth: "420px" }}>
          {/* Bear sprite: align inside the left edge, can overflow on top */}
          <div className="absolute left-2 -top-10 z-50" style={{ pointerEvents: "none" }}>
            <ToastBearSprite state={bear.state} src={bear.src} size={96} />
          </div>
          {/* Close button moved to top-right for intuitiveness */}
          <div className="absolute right-1 top-1 z-50" style={{ pointerEvents: "auto" }}>
            <RadixToast.Close aria-label="Close" asChild>
              <button
                className="flex items-center justify-center rounded p-1.5 text-muted-foreground transition-[color,background-color,transform] hover:bg-white/10 hover:text-white active:scale-95"
                data-close
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </RadixToast.Close>
          </div>
          <div
            className="flex min-h-[96px] w-full items-start justify-between gap-3 pt-20"
            style={{ pointerEvents: "none" }}
          >
            <div className="min-w-0 flex-1">
              <ToastText className="font-pressStart2P text-[10px] uppercase text-foreground">
                {t.title}
              </ToastText>
              {t.subtitle ? (
                <ToastText className="mt-0.5 font-pressStart2P text-[8px] uppercase text-muted-foreground">
                  {t.subtitle}
                  {t.count && t.count.total > 1 ? (
                    <span className="ml-2 text-green-400">
                      [{t.count.current}/{t.count.total}]
                    </span>
                  ) : null}
                </ToastText>
              ) : null}
            </div>
          </div>
        </div>
      </AnimatedToastContent>
    </RadixToast.Root>
  );
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = React.useState<Record<string, ToastData>>({});

  const upsert = React.useCallback((toast: Omit<ToastData, "open"> & { open?: boolean }) => {
    setToasts((prev) => {
      const open = toast.open ?? true;
      return { ...prev, [toast.id]: { ...prev[toast.id], ...toast, open } as ToastData };
    });
  }, []);

  const dismiss = React.useCallback((id: string) => {
    setToasts((prev) => {
      const copy = { ...prev };
      delete copy[id];
      return copy;
    });
  }, []);

  return (
    <ToastContext.Provider value={{ upsert, dismiss }}>
      <RadixToast.Provider swipeDirection="right">
        {children}
        {Object.values(toasts).map((t) => (
          <ToastItem
            key={t.id}
            t={t}
            onOpenChange={(open) => {
              if (!open) {
                // Allow Radix to animate the close by first updating open=false,
                // then remove the toast shortly after (matched to CSS toastOut).
                upsert({ ...t, open: false });
                setTimeout(() => dismiss(t.id), 460);
              }
            }}
          />
        ))}
        <RadixToast.Viewport className="ToastViewport" />
      </RadixToast.Provider>
    </ToastContext.Provider>
  );
}
